/**
 * Quiz gameplay module.
 * Exports: renderQuiz(modal, data, onComplete), teardownQuiz(modal)
 */

import imgGameIcon from '../../images/game-icon.svg';
import { renderGameMessage, startBlockedMessage } from '../shared/game-message';
import { ICON_CLOSE } from '../../js/game-icons';

export const WELCOME_CONFIG = {
    icon: imgGameIcon,
    title: 'Witaj w Quizie! 🎯✨',
    description: '<p>Przygotuj się na grę, która sprawdzi Twoją wiedzę i refleks!</p>' +
        '<p>Twoim zadaniem jest odpowiadanie na pytania i wybieranie poprawnych odpowiedzi spośród dostępnych opcji.</p>' +
        '<p>Uważaj jednak – czas ma znaczenie. Im szybciej odpowiesz, tym większa szansa na lepszy wynik!</p>',
    howToPlay: {
        heading: 'JAK GRAĆ?',
        body: '<ol>' +
            '<li>Odpowiadaj na pytania – mogą pojawiać się w różnej formie. </li>' +
            '<li>Wybierz jedną poprawną odpowiedź spośród 3 opcji.</li>' +
            '<li>Działaj szybko – każda sekunda ma znaczenie!</li>' +
            '</ol>',
    },
    scoring: {
        heading: 'PUNKTACJA',
        body: '<ul>' +
            '<li>✅ +5 punkt za każdą poprawną odpowiedź</li>' +
            '<li>❌ -1 punkt za błędną odpowiedź</li>' +
            '<li>❌ 0 punktów za brak odpowiedzi (w tym za upłynięcie czasu)</li>' +
            '</ul>' +
            '<p>Maksymalny wynik to 30 punktów.</p>',
    },
};

const ICON_PLAY = `<svg class="quiz-icon quiz-icon--play" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="50" cy="50" r="50" fill="#FFFFF0" fill-opacity="0.5"/><path d="M40 32 L72 50 L40 68 Z" fill="#000"/></svg>`;
const ICON_CHECK = `<svg class="quiz-icon quiz-icon--check" viewBox="0 0 89 71" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M5 38 L33 64 L84 8" stroke="#0A0203" stroke-width="14" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>`;
const ICON_CROSS = `<svg class="quiz-icon quiz-icon--cross" viewBox="0 0 57 57" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M8 8 L49 49 M49 8 L8 49" stroke="#FFFFF0" stroke-width="8" stroke-linecap="round"/></svg>`;

let currentQuestions = [];
let currentAnswers   = [];
let currentTaskId    = null;
let sessionToken     = null;
let gameStartTime    = null;
let quizTimer        = null;
let onCompleteCb     = null;
let currentState     = null;

function stopTimer() {
    if (quizTimer) { clearInterval(quizTimer); quizTimer = null; }
}

function startTimer(modal, seconds, onExpire) {
    stopTimer();
    let remaining = seconds;
    renderTimer(modal, remaining);
    quizTimer = setInterval(() => {
        remaining--;
        renderTimer(modal, remaining);
        if (remaining <= 0) { stopTimer(); onExpire(); }
    }, 1000);
}

function renderTimer(modal, seconds) {
    const el = modal.querySelector('.quiz-question__timer');
    if (!el) return;
    const safe = Math.max(0, seconds);
    const m = Math.floor(safe / 60);
    const s = safe % 60;
    el.textContent = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    el.classList.toggle('is-urgent', safe <= 10);
}

function content(modal) {
    return modal.querySelector('.game-modal__content');
}

function escHtml(str) {
    return String(str ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function userPoints() {
    const hud = document.querySelector('.s-game__pts-box--ranking .s-game__pts-value');
    const v = hud ? parseInt(hud.textContent, 10) : 0;
    return Number.isFinite(v) ? v : 0;
}

function renderHud(modal, idx) {
    const total = currentQuestions.length;
    return `
        <div class="quiz-hud">
            <div class="quiz-hud__pill">
                <span class="quiz-hud__pill-label">Punkty:</span>
                <span class="quiz-hud__pill-value">${userPoints()}</span>
            </div>
            <div class="quiz-hud__pill">
                <span class="quiz-hud__pill-label">Pytanie:</span>
                <span class="quiz-hud__pill-value">${idx + 1}/${total}</span>
            </div>
            <button type="button" class="quiz-hud__close js-game-modal-close" aria-label="Zamknij">${ICON_CLOSE}</button>
        </div>
    `;
}

function youtubeIdFromUrl(url) {
    if (!url) return null;
    const m = String(url).match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
    return m ? m[1] : null;
}

function mediaHtml(q) {
    if (q.mediaType === 'image' && q.imageUrl) {
        return `<div class="quiz-question__media-image"><img src="${escHtml(q.imageUrl)}" alt=""></div>`;
    }
    if ((q.mediaType === 'youtube' || q.mediaType === 'video') && q.youtubeUrl) {
        const id = youtubeIdFromUrl(q.youtubeUrl);
        if (id) {
            const poster = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
            return `
                <div class="quiz-question__media-video">
                    <button type="button"
                            class="quiz-yt-poster"
                            data-action="play-yt"
                            data-yt-id="${escHtml(id)}"
                            style="background-image: url('${escHtml(poster)}')"
                            aria-label="Odtwórz wideo">
                        ${ICON_PLAY}
                    </button>
                </div>
            `;
        }
    }
    return '';
}

function answersHtml(q) {
    const valid = (q.answers || []).filter(a => String(a.text || '').trim() !== '' || (a.imageUrl && String(a.imageUrl).trim() !== ''));
    const isBool = valid.length === 2;
    const layoutClass = isBool ? 'quiz-answer--bool' : 'quiz-answer--abc';
    const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

    return valid.map((a, i) => {
        const prefix = isBool ? '' : `<span class="quiz-answer__letter">${letters[i] || ''}.</span> `;
        const text = String(a.text || '').trim();
        const image = a.imageUrl && String(a.imageUrl).trim() !== ''
            ? `<span class="quiz-answer__image"><img src="${escHtml(a.imageUrl)}" alt=""></span>`
            : '';
        const textHtml = text !== '' ? `<span class="quiz-answer__text">${escHtml(text)}</span>` : '';
        return `
            <button type="button"
                    class="quiz-answer ${layoutClass} js-quiz-answer"
                    data-answer-id="${a.id}"
                    aria-pressed="false">
                <span class="quiz-answer__icon quiz-answer__icon--check">${ICON_CHECK}</span>
                ${prefix}${image}${textHtml}
                <span class="quiz-answer__icon quiz-answer__icon--cross">${ICON_CROSS}</span>
            </button>
        `;
    }).join('');
}

function renderQuestion(modal, idx) {
    currentState = { type: 'question', idx };
    const q = currentQuestions[idx];

    const description = String(q.description || '').trim();

    content(modal).innerHTML = `
        ${renderHud(modal, idx)}
        <div class="quiz-question">
            <div class="quiz-question__inner">
                ${q.timeLimit > 0 ? `<div class="quiz-question__timer">--:--</div>` : ''}
                <h2 class="quiz-question__heading">${escHtml(q.text || '')}</h2>
                ${description ? `<p class="quiz-question__description">${escHtml(description)}</p>` : ''}
                ${mediaHtml(q)}
                <div class="quiz-question__answers">
                    ${answersHtml(q)}
                </div>
            </div>
            <button type="button" class="quiz-cta js-quiz-submit" disabled data-action="submit-answer" data-idx="${idx}">
                ${idx + 1 < currentQuestions.length ? 'DALEJ' : 'WYŚLIJ'}
            </button>
        </div>
    `;

    if (q.timeLimit > 0) {
        startTimer(modal, q.timeLimit, () => advance(modal, idx, []));
    }
}

function renderError(modal, msg) {
    content(modal).innerHTML = `
        <div class="quiz-error">
            <p class="quiz-error__msg">${escHtml(msg)}</p>
            <button type="button" class="quiz-result__close js-game-close-results">ZAMKNIJ</button>
        </div>
    `;
}

async function revealFeedback(modal, fromIdx, answerIds) {
    const q = currentQuestions[fromIdx];
    if (!q) return;

    let correctIds = [];
    try {
        const elapsedMs = gameStartTime !== null ? Date.now() - gameStartTime : null;
        const res = await fetch(`/api/task/${currentTaskId}/quiz/check/${q.id}`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ sessionToken, answerIds: Array.isArray(answerIds) ? answerIds : [], elapsedMs }),
        });
        if (res.ok) {
            const data = await res.json();
            correctIds = Array.isArray(data.correctAnswerIds) ? data.correctAnswerIds.map(Number) : [];
        }
    } catch {
        return;
    }

    const submitBtn = modal.querySelector('.js-quiz-submit');
    if (submitBtn) submitBtn.disabled = true;

    const submittedSet = new Set((Array.isArray(answerIds) ? answerIds : []).map(Number));
    const correctSet = new Set(correctIds);

    modal.querySelectorAll('.js-quiz-answer').forEach(btn => {
        const id = parseInt(btn.dataset.answerId, 10);
        btn.classList.remove('is-selected');
        btn.style.pointerEvents = 'none';
        if (correctSet.has(id)) {
            btn.classList.add('is-correct');
        } else if (submittedSet.has(id)) {
            btn.classList.add('is-incorrect');
        }
    });

    await new Promise(resolve => setTimeout(resolve, 1500));
}

async function advance(modal, fromIdx, answerIds) {
    stopTimer();
    currentAnswers[fromIdx] = {
        questionId: currentQuestions[fromIdx].id,
        answerIds:  Array.isArray(answerIds) ? answerIds : [],
    };

    if (Array.isArray(answerIds) && answerIds.length > 0) {
        await revealFeedback(modal, fromIdx, answerIds);
    }

    const nextIdx = fromIdx + 1;
    if (nextIdx < currentQuestions.length) {
        renderQuestion(modal, nextIdx);
    } else {
        submitQuiz(modal);
    }
}

async function submitQuiz(modal) {
    content(modal).innerHTML = `
        <div class="quiz-loading">
            <p class="quiz-loading__msg">Wysyłanie…</p>
        </div>
    `;

    const payload = { sessionToken };

    try {
        const res  = await fetch(`/api/task/${currentTaskId}/quiz/submit`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify(payload),
        });
        const data = await res.json();

        if (res.ok) {
            if (onCompleteCb) onCompleteCb(data);
            document.dispatchEvent(new CustomEvent('game-modal:result-show', {
                detail: {
                    rankingPoints: data.totalRankingPoints ?? 0,
                    redeemablePoints: data.totalRedeemablePoints ?? 0,
                },
            }));
        } else if (res.status === 409) {
            renderError(modal, 'To zadanie zostało już zaliczone.');
        } else {
            renderError(modal, 'Wystąpił błąd. Spróbuj ponownie.');
        }
    } catch {
        renderError(modal, 'Błąd sieci. Sprawdź połączenie.');
    }
}

function handleAnswerClick(modal, btn) {
    // Single-select: one answer per question, whatever the layout. Picking a new
    // option replaces the previous one; re-clicking the selected one keeps it.
    modal.querySelectorAll('.js-quiz-answer').forEach(b => {
        b.classList.remove('is-selected');
        b.setAttribute('aria-pressed', 'false');
    });
    btn.classList.add('is-selected');
    btn.setAttribute('aria-pressed', 'true');

    const submitBtn = modal.querySelector('.js-quiz-submit');
    if (submitBtn) submitBtn.disabled = false;
}

function onModalClick(e) {
    const modal = e.currentTarget;

    const action = e.target.closest('[data-action]')?.dataset.action;
    if (action === 'play-yt') {
        const btn = e.target.closest('[data-action="play-yt"]');
        if (!btn) return;
        const id = btn.dataset.ytId;
        if (!id) return;
        const params = new URLSearchParams({
            autoplay:       '1',
            playsinline:    '1',
            rel:            '0',
            modestbranding: '1',
            controls:       '1',
        });
        const wrap = btn.parentElement;
        wrap.innerHTML = `<iframe src="https://www.youtube.com/embed/${id}?${params}"
                                 title="Wideo"
                                 frameborder="0"
                                 playsinline
                                 referrerpolicy="strict-origin-when-cross-origin"
                                 allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                 allowfullscreen></iframe>`;
        return;
    }

    if (action === 'submit-answer') {
        const btn = e.target.closest('[data-action="submit-answer"]');
        if (!btn || btn.disabled) return;
        const idx = parseInt(btn.dataset.idx, 10);
        const selected = modal.querySelectorAll('.js-quiz-answer.is-selected');
        const ids = Array.from(selected).map(b => parseInt(b.dataset.answerId, 10));
        advance(modal, idx, ids);
        return;
    }

    const answerBtn = e.target.closest('.js-quiz-answer');
    if (answerBtn) {
        handleAnswerClick(modal, answerBtn);
        return;
    }
}

export async function renderQuiz(modal, data, onComplete) {
    currentQuestions = (data.quiz.questions || []).filter(q => String(q.text || '').trim() !== '');
    currentAnswers   = new Array(currentQuestions.length).fill(null);
    currentTaskId    = data.task.id;
    onCompleteCb     = onComplete;

    if (currentQuestions.length === 0) {
        renderError(modal, 'Ten quiz nie ma żadnych pytań.');
        return;
    }

    modal.classList.add('game-modal--quiz');
    modal.addEventListener('click', onModalClick);

    content(modal).innerHTML = `<div class="quiz-loading"><p class="quiz-loading__msg">Ładowanie…</p></div>`;

    try {
        const res = await fetch(`/api/task/${currentTaskId}/quiz/start`, {
            method:      'POST',
            headers:     { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
        });
        if (res.status === 409) {
            const body = await res.json().catch(() => ({}));
            renderGameMessage(modal, startBlockedMessage(body?.error));
            return;
        }
        if (!res.ok) {
            renderError(modal, 'Błąd rozpoczęcia gry.');
            return;
        }
        const startData = await res.json();
        sessionToken  = startData.sessionToken ?? null;
        gameStartTime = Date.now();
    } catch {
        renderError(modal, 'Błąd połączenia.');
        return;
    }

    renderQuestion(modal, 0);
}

export async function forceEndQuiz() {
    if (!currentTaskId || !sessionToken) return;
    try {
        const res = await fetch(`/api/task/${currentTaskId}/quiz/abandon`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ sessionToken }),
        });
        if (res.ok) {
            const data = await res.json();
            document.dispatchEvent(new CustomEvent('game-modal:result-show', {
                detail: {
                    rankingPoints:    data.rankingPointsEarned    ?? 0,
                    redeemablePoints: data.redeemablePointsEarned ?? 0,
                },
            }));
        }
    } catch { /* ignore */ }
}

export function teardownQuiz(modal) {
    stopTimer();
    modal.removeEventListener('click', onModalClick);
    modal.classList.remove('game-modal--quiz');
    currentQuestions = [];
    currentAnswers   = [];
    currentTaskId    = null;
    sessionToken     = null;
    gameStartTime    = null;
    onCompleteCb     = null;
    currentState     = null;
}
